import { create } from 'zustand';
import {
  collection,
  doc,
  addDoc,
  updateDoc,
  deleteDoc,
  getDoc,
  getDocs,
  query,
  where,
  orderBy,
  onSnapshot,
  serverTimestamp
} from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL, getBlob, deleteObject, listAll } from 'firebase/storage';
import { httpsCallable } from 'firebase/functions';
import { db, storage, functions } from '../services/firebase';
import { COLLECTIONS } from '../services/firestore';

export const useMarkingStore = create((set, get) => ({
  // State
  markingJobs: [],
  markingTasks: [],
  markingQuestions: [],
  currentJob: null,
  isLoading: false,
  isUploading: false,
  isMarking: false,
  error: null,

  // Create a new marking job in Firestore
  createMarkingJob: async (jobData) => {
    try {
      set({ isLoading: true, error: null });

      const docRef = await addDoc(collection(db, COLLECTIONS.MARKING_JOBS), {
        ...jobData,
        status: 'uploading',
        scriptFiles: [],
        memoFiles: [],
        // memoText is spread from jobData when the user pastes text
        markedPdfPath: null,
        results: null,
        error: null,
        progress: { currentPage: 0, totalPages: 0, stage: 'uploading' },
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        completedAt: null
      });

      const job = {
        id: docRef.id,
        ...jobData,
        status: 'uploading',
        createdAt: new Date(),
        updatedAt: new Date()
      };

      set(state => ({
        markingJobs: [job, ...state.markingJobs],
        currentJob: job,
        isLoading: false
      }));

      return docRef.id;
    } catch (error) {
      console.error('Error creating marking job:', error);
      set({ error: error.message, isLoading: false });
      throw error;
    }
  },

  loadMarkingTasks: async (userId) => {
    try {
      const q = query(
        collection(db, COLLECTIONS.MARKING_TASKS),
        where('userId', '==', userId),
        orderBy('createdAt', 'desc')
      );
      const snapshot = await getDocs(q);
      const tasks = snapshot.docs.map((d) => ({
        id: d.id,
        ...d.data(),
        createdAt: d.data().createdAt?.toDate() || new Date(),
        updatedAt: d.data().updatedAt?.toDate() || new Date()
      }));
      set({ markingTasks: tasks });
      return tasks;
    } catch (error) {
      console.error('Error loading marking tasks:', error);
      set({ error: error.message });
      throw error;
    }
  },

  loadMarkingQuestions: async (userId) => {
    try {
      const q = query(
        collection(db, COLLECTIONS.MARKING_QUESTIONS),
        where('userId', '==', userId),
        orderBy('createdAt', 'desc')
      );
      const snapshot = await getDocs(q);
      const questions = snapshot.docs.map((d) => ({
        id: d.id,
        ...d.data(),
        createdAt: d.data().createdAt?.toDate() || new Date(),
        updatedAt: d.data().updatedAt?.toDate() || new Date()
      }));
      set({ markingQuestions: questions });
      return questions;
    } catch (error) {
      console.error('Error loading marking questions:', error);
      set({ error: error.message });
      throw error;
    }
  },

  createMarkingTask: async (taskData) => {
    try {
      const docRef = await addDoc(collection(db, COLLECTIONS.MARKING_TASKS), {
        ...taskData,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });
      const created = {
        id: docRef.id,
        ...taskData,
        createdAt: new Date(),
        updatedAt: new Date()
      };
      set((state) => ({ markingTasks: [created, ...state.markingTasks] }));
      return created;
    } catch (error) {
      console.error('Error creating marking task:', error);
      set({ error: error.message });
      throw error;
    }
  },

  createMarkingQuestion: async (questionData) => {
    try {
      const docRef = await addDoc(collection(db, COLLECTIONS.MARKING_QUESTIONS), {
        ...questionData,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });
      const created = {
        id: docRef.id,
        ...questionData,
        createdAt: new Date(),
        updatedAt: new Date()
      };
      set((state) => ({ markingQuestions: [created, ...state.markingQuestions] }));
      return created;
    } catch (error) {
      console.error('Error creating marking question:', error);
      set({ error: error.message });
      throw error;
    }
  },

  deleteMarkingTask: async (taskId) => {
    try {
      await deleteDoc(doc(db, COLLECTIONS.MARKING_TASKS, taskId));
      set((state) => ({
        markingTasks: state.markingTasks.filter((t) => t.id !== taskId)
      }));
    } catch (error) {
      console.error('Error deleting marking task:', error);
      set({ error: error.message });
      throw error;
    }
  },

  deleteMarkingQuestion: async (questionId) => {
    try {
      await deleteDoc(doc(db, COLLECTIONS.MARKING_QUESTIONS, questionId));
      set((state) => ({
        markingQuestions: state.markingQuestions.filter((q) => q.id !== questionId)
      }));
    } catch (error) {
      console.error('Error deleting marking question:', error);
      set({ error: error.message });
      throw error;
    }
  },

  // Upload script and memo files (PDF or images) to Storage
  uploadFiles: async (
    jobId,
    userId,
    scriptFiles,
    memoFiles,
    statusAfterUpload = 'processing',
    scriptMetaByIndex = [],
    paperFiles = []
  ) => {
    try {
      set({ isUploading: true, error: null });

      const uploadGroup = async (files, folder) => {
        const entries = [];
        await Promise.all(files.map(async (file, idx) => {
          const ext = file.name.split('.').pop().toLowerCase();
          const inferredMimeTypes = {
            pdf: 'application/pdf',
            jpg: 'image/jpeg',
            jpeg: 'image/jpeg',
            png: 'image/png',
            webp: 'image/webp'
          };
          const mimeType = file.type || inferredMimeTypes[ext] || 'application/octet-stream';
          const fileName = `${idx}.${ext}`;
          const path = `marking/${userId}/${jobId}/${folder}/${fileName}`;
          const fileRef = ref(storage, path);
          await uploadBytes(fileRef, file, { contentType: mimeType });
          entries.push({ path, mimeType, name: file.name, index: idx });
        }));
        // Sort by index to maintain order
        entries.sort((a, b) => a.index - b.index);
        return entries;
      };

      const [scriptEntries, memoEntries, paperEntries] = await Promise.all([
        uploadGroup(scriptFiles, 'script'),
        uploadGroup(memoFiles, 'memo'),
        uploadGroup(paperFiles, 'paper')
      ]);

      const scriptPaths = scriptEntries.map(e => ({ path: e.path, mimeType: e.mimeType }));
      const memoPaths = memoEntries.map(e => ({ path: e.path, mimeType: e.mimeType }));

      const metaMap = new Map(
        (Array.isArray(scriptMetaByIndex) ? scriptMetaByIndex : []).map((meta) => [Number(meta.index), meta])
      );
      const scriptEntriesWithMeta = scriptEntries.map((entry) => {
        const meta = metaMap.get(Number(entry.index)) || {};
        return {
          ...entry,
          learnerId: meta.learnerId || null,
          learnerName: meta.learnerName || '',
          learnerNumber: meta.learnerNumber || null,
          questionId: meta.questionId || null,
          questionLabel: meta.questionLabel || null
        };
      });

      // Update the Firestore doc with file metadata
      const { updateDoc } = await import('firebase/firestore');
      const jobRef = doc(db, COLLECTIONS.MARKING_JOBS, jobId);
      await updateDoc(jobRef, {
        scriptFiles: scriptPaths,
        scriptEntries: scriptEntriesWithMeta,
        memoFiles: memoPaths,
        ...(paperEntries.length ? { paperFiles: paperEntries.map(({ path, mimeType }) => ({ path, mimeType })) } : {}),
        status: statusAfterUpload,
        updatedAt: serverTimestamp()
      });

      set(state => ({
        currentJob: state.currentJob?.id === jobId
          ? {
            ...state.currentJob,
            scriptFiles: scriptPaths,
            scriptEntries: scriptEntriesWithMeta,
            memoFiles: memoPaths,
            status: statusAfterUpload
          }
          : state.currentJob,
        isUploading: false
      }));

      return { scriptPaths, memoPaths };
    } catch (error) {
      console.error('Error uploading files:', error);
      set({ error: error.message, isUploading: false });
      throw error;
    }
  },

  // Call the Cloud Function to start marking
  startMarking: async (jobId, scriptPaths, memoPaths, memoText, markingType = 'ai-direct') => {
    try {
      set({ isMarking: true, error: null });

      if (markingType === 'ai-collaborative') {
        const result = await get().runAiWorkflow(jobId, 'prepare', {}, 0);
        set({ isMarking: false });
        return result;
      }
      const markFn = httpsCallable(functions, 'markStudentScripts');
      const payload = { jobId, scriptPaths, memoPaths, markingType };
      if (memoText) payload.memoText = memoText;
      const result = await markFn(payload);

      set({ isMarking: false });
      return result.data;
    } catch (error) {
      console.error('Error starting marking:', error);
      set({ error: error.message, isMarking: false });
      throw error;
    }
  },

  runAiWorkflow: async (jobId, action, payload = {}, revision) => {
    set({ error: null });
    const job = get().currentJob?.id === jobId ? get().currentJob : get().markingJobs.find((j) => j.id === jobId);
    const call = httpsCallable(functions, 'aiMarkingWorkflow', { timeout: 720000 });
    const result = await call({ jobId, action, payload, revision: revision ?? job?.aiWorkflow?.revision ?? 0 });
    if (get().currentJob?.id === jobId) {
      await get().loadMarkingJob(jobId);
    }
    return result.data;
  },

  loadModerationJobs: async (userId) => {
    const snapshot = await getDocs(query(collection(db, COLLECTIONS.MARKING_JOBS), where('aiWorkflow.moderatorId', '==', userId)));
    return snapshot.docs.map((d) => ({ id: d.id, ...d.data(), createdAt: d.data().createdAt?.toDate() || null }));
  },

  loadAiAudit: async (jobId) => {
    const snapshot = await getDocs(query(collection(db, COLLECTIONS.MARKING_JOBS, jobId, 'ai-audit'), orderBy('revision', 'asc')));
    return snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
  },

  extractQuestionMetadata: async ({ imageBase64, mimeType, guidance = '' }) => {
    try {
      const extractFn = httpsCallable(functions, 'extractQuestionMetadata');
      const result = await extractFn({ imageBase64, mimeType, guidance });
      return result.data?.question || null;
    } catch (error) {
      console.error('Error extracting question metadata:', error);
      set({ error: error.message });
      throw error;
    }
  },

  // Load all marking jobs for a user
  loadMarkingJobs: async (userId) => {
    try {
      set({ isLoading: true, error: null });

      const q = query(
        collection(db, COLLECTIONS.MARKING_JOBS),
        where('userId', '==', userId),
        orderBy('createdAt', 'desc')
      );

      const snapshot = await getDocs(q);
      const jobs = snapshot.docs.map(d => ({
        id: d.id,
        ...d.data(),
        createdAt: d.data().createdAt?.toDate() || new Date(),
        updatedAt: d.data().updatedAt?.toDate() || new Date(),
        completedAt: d.data().completedAt?.toDate() || null
      }));

      set({ markingJobs: jobs, isLoading: false });
      return jobs;
    } catch (error) {
      console.error('Error loading marking jobs:', error);
      set({ error: error.message, isLoading: false });
      throw error;
    }
  },

  // Load a single marking job
  loadMarkingJob: async (jobId) => {
    try {
      set({ isLoading: true, error: null });

      const docSnap = await getDoc(doc(db, COLLECTIONS.MARKING_JOBS, jobId));
      if (!docSnap.exists()) throw new Error('Marking job not found');

      const job = {
        id: docSnap.id,
        ...docSnap.data(),
        createdAt: docSnap.data().createdAt?.toDate() || new Date(),
        updatedAt: docSnap.data().updatedAt?.toDate() || new Date(),
        completedAt: docSnap.data().completedAt?.toDate() || null
      };

      set({ currentJob: job, isLoading: false });
      return job;
    } catch (error) {
      console.error('Error loading marking job:', error);
      set({ error: error.message, isLoading: false });
      throw error;
    }
  },

  // Mark a job as failed (used when the cloud function call itself fails)
  failJob: async (jobId, errorMessage) => {
    try {
      const { updateDoc } = await import('firebase/firestore');
      const jobRef = doc(db, COLLECTIONS.MARKING_JOBS, jobId);
      await updateDoc(jobRef, {
        status: 'failed',
        error: errorMessage,
        updatedAt: serverTimestamp()
      });
      set(state => ({
        currentJob: state.currentJob?.id === jobId
          ? { ...state.currentJob, status: 'failed', error: errorMessage }
          : state.currentJob,
        isMarking: false
      }));
    } catch (err) {
      console.error('Error updating job to failed:', err);
    }
  },

  saveManualResults: async (jobId, resultData, markedPdfBytes = null, ownerId = null) => {
    try {
      let markedPdfPath = null;
      if (markedPdfBytes) {
        const resolvedOwnerId = ownerId
          || (get().currentJob?.id === jobId ? get().currentJob?.userId : null)
          || get().markingJobs.find((job) => job.id === jobId)?.userId;

        if (!resolvedOwnerId) {
          throw new Error('Unable to resolve marking job owner for manual PDF upload.');
        }

        markedPdfPath = `marking/${resolvedOwnerId}/${jobId}/marked.pdf`;
        await uploadBytes(ref(storage, markedPdfPath), markedPdfBytes, {
          contentType: 'application/pdf'
        });

        await updateDoc(doc(db, COLLECTIONS.MARKING_JOBS, jobId), {
          status: 'completed',
          results: resultData,
          markedPdfPath,
          completedAt: serverTimestamp(),
          updatedAt: serverTimestamp()
        });
      } else {
        const finalizeFn = httpsCallable(functions, 'finalizeManualMarking');
        const response = await finalizeFn({ jobId, resultData });
        markedPdfPath = response.data?.markedPdfPath || null;
      }

      set(state => ({
        currentJob: state.currentJob?.id === jobId
          ? { ...state.currentJob, results: resultData, status: 'completed', markedPdfPath: markedPdfPath || state.currentJob.markedPdfPath }
          : state.currentJob,
        markingJobs: state.markingJobs.map(j =>
          j.id === jobId ? { ...j, results: resultData, status: 'completed', markedPdfPath: markedPdfPath || j.markedPdfPath } : j
        )
      }));
    } catch (error) {
      console.error('Error saving manual results:', error);
      set({ error: error.message });
      throw error;
    }
  },

  createModerationJob: async (sourceJob, userId) => {
    if (!sourceJob?.id || sourceJob.status !== 'completed') {
      throw new Error('Only completed marking jobs can be reopened for moderation.');
    }
    if (!sourceJob.markedPdfPath) {
      throw new Error('This completed job has no marked document to moderate.');
    }
    if (!userId || sourceJob.userId !== userId) {
      throw new Error('You can only moderate your own marking jobs.');
    }

    let newJobId = null;
    try {
      set({ isUploading: true, error: null });
      const markedBlob = await getBlob(ref(storage, sourceJob.markedPdfPath));
      const sourceFile = new File(
        [markedBlob],
        `marked-${sourceJob.id}.pdf`,
        { type: 'application/pdf' }
      );

      newJobId = await get().createMarkingJob({
        userId,
        title: `Moderation - ${sourceJob.title || 'Marked Script'}`,
        mode: 'single-learner',
        subject: sourceJob.subject || '',
        grade: sourceJob.grade || '',
        totalMarks: sourceJob.totalMarks || sourceJob.results?.maxScore || null,
        learnerName: sourceJob.learnerName || '',
        learnerId: sourceJob.learnerId || null,
        learnerNumber: sourceJob.learnerNumber || null,
        markerName: sourceJob.markerName || null,
        markerEmail: sourceJob.markerEmail || null,
        markingType: 'manual',
        taskId: sourceJob.taskId || null,
        questionId: sourceJob.questionId || null,
        questionLabel: sourceJob.questionLabel || null,
        taskData: sourceJob.taskData || null,
        workflowSource: 'remark-moderation',
        sourceJobId: sourceJob.id,
        moderationContext: {
          sourceJobId: sourceJob.id,
          previousPercentage: sourceJob.results?.percentage ?? null,
          previousTotalScore: sourceJob.results?.totalScore ?? null,
          previousMaxScore: sourceJob.results?.maxScore ?? null
        }
      });

      await get().uploadFiles(
        newJobId,
        userId,
        [sourceFile],
        [],
        'manual-review'
      );
      set({ isUploading: false });
      return newJobId;
    } catch (error) {
      console.error('Error creating moderation job:', error);
      set({ isUploading: false, error: error.message });
      if (newJobId) await get().failJob(newJobId, error.message || 'Failed to prepare moderation job');
      throw error;
    }
  },

  // Subscribe to real-time updates for a job
  subscribeToJob: (jobId) => {
    const jobRef = doc(db, COLLECTIONS.MARKING_JOBS, jobId);
    const unsubscribe = onSnapshot(jobRef, (docSnap) => {
      if (docSnap.exists()) {
        const job = {
          id: docSnap.id,
          ...docSnap.data(),
          createdAt: docSnap.data().createdAt?.toDate() || new Date(),
          updatedAt: docSnap.data().updatedAt?.toDate() || new Date(),
          completedAt: docSnap.data().completedAt?.toDate() || null
        };
        set(state => ({
          currentJob: job,
          markingJobs: state.markingJobs.map(j => j.id === jobId ? job : j),
          isMarking: !['completed', 'failed', 'ai-review'].includes(job.status)
        }));
      }
    }, (error) => {
      console.error('Snapshot listener error:', error);
      set({ error: 'Lost connection to marking job. Refresh to retry.' });
    });
    return unsubscribe;
  },

  // Delete a marking job and all its storage files
  deleteMarkingJob: async (jobId, userId) => {
    try {
      set({ isLoading: true, error: null });

      // Delete all files under the job folder
      const folders = ['script', 'memo', 'paper'];
      for (const folder of folders) {
        try {
          const folderRef = ref(storage, `marking/${userId}/${jobId}/${folder}`);
          const list = await listAll(folderRef);
          await Promise.all(list.items.map(item => deleteObject(item).catch(() => {})));
        } catch (e) {
          // Folder may not exist
        }
      }
      // Delete marked PDF
      await deleteObject(ref(storage, `marking/${userId}/${jobId}/marked.pdf`)).catch(() => {});

      await deleteDoc(doc(db, COLLECTIONS.MARKING_JOBS, jobId));

      set(state => ({
        markingJobs: state.markingJobs.filter(j => j.id !== jobId),
        currentJob: state.currentJob?.id === jobId ? null : state.currentJob,
        isLoading: false
      }));
    } catch (error) {
      console.error('Error deleting marking job:', error);
      set({ error: error.message, isLoading: false });
      throw error;
    }
  },

  // Get download URL for the marked PDF
  getMarkedPdfUrl: async (path) => {
    try {
      return await getDownloadURL(ref(storage, path));
    } catch (error) {
      console.error('Error getting marked PDF URL:', error);
      throw error;
    }
  },

  clearCurrentJob: () => set({ currentJob: null }),
  clearError: () => set({ error: null })
}));
